import React, { memo } from "react";

import { IconOld, Icon } from "@partials";
import { ExchangeRatesTableType } from "@constants";
import { getLocalization } from "@utilities";

import { ExchangeTableRowItem, LocalizationTexts } from "../types";

export interface TableHintProps {
  localization: LocalizationTexts;
  type: ExchangeRatesTableType;
}

type RatesTableProps = {
  items: ExchangeTableRowItem[];
  localization: LocalizationTexts;
  type: ExchangeRatesTableType;
  iconsList: any;
  baseUrl: string;
  queryDateValueString: string;
};

const TableHint: React.FC<TableHintProps> = ({ type, localization }) => {
  return (
    <div className="exchange-rates-hint">
      <span className="font-bold">
        {getLocalization(
          localization,
          type === ExchangeRatesTableType.Cash ? "ExchangeCash" : "ExchangeWire"
        )}
      </span>{" "}
      &nbsp;-{" "}
      <span dangerouslySetInnerHTML={{ __html: getLocalization(localization, type === ExchangeRatesTableType.Cash ? "ExchangeCash_Note" : "ExchangeWire_Note") }}>
      </span>
    </div>
  );
};

const RatesTable: React.FC<RatesTableProps> = ({
  items,
  localization,
  type,
  iconsList,
  baseUrl,
  queryDateValueString,
}) => {
  if (!items) return null;

  const { arrowRight } = iconsList;

  return (
    <>
      <TableHint type={type} localization={localization} />

      <div className="table-exrate" data-id={type} data-testid={`rates-table-${type}`}>
        <div className="table-exrate-header">
          <div className="table-exrate-row">
            <div className="table-exrate-cell table-exrate-cell--header table-exrate-cell--empty-country-name" />
            <div className="table-exrate-cell table-exrate-cell--header">
              {getLocalization(localization, "Currency")}
            </div>
            <div className="table-exrate-cell table-exrate-cell--header">
              {getLocalization(localization, "Buy")}
            </div>
            <div className="table-exrate-cell table-exrate-cell--header">
              {getLocalization(localization, "Sell")}
            </div>
            <div className="table-exrate-cell table-exrate-cell--header">
              {getLocalization(localization, "CnbMiddle")}
            </div>
            <div className="table-exrate-cell table-exrate-cell--header table-exrate-cell--empty-icon" />
          </div>
        </div>

        <div className="table-exrate__body">
          {items.map((item) => {
            const detailUrlDateTime = `${baseUrl}${item.detailUrl}&date=${queryDateValueString}`;

            return (
              <a
                href={detailUrlDateTime}
                className="table-exrate-link"
                data-testid="rates-table-link"
                key={item.id}
                title={item.title}
              >
                <div className="table-exrate-row">
                  <div className="table-exrate-cell table-exrate-country-name">
                    <div>
                      <IconOld name={item.icon} altText={getLocalization(localization, "FlagLabel")} className="mr-4 inline h-8 w-8 sm:mx-4" />
                      <span className="group-hover:text-red-corporate group-hover:underline sm:font-semibold">
                        {item.title}
                      </span>
                    </div>
                    <div className="table-exrate-icon-arrow-mobile">
                      <Icon
                        name={arrowRight.name}
                        viewBox={arrowRight.viewbox}
                        alt={arrowRight.alt}
                        svg={arrowRight.svg}
                        className="inline h-8 w-8 fill-red-corporate"
                      />
                    </div>
                  </div>
                  <div
                    className="table-exrate-cell"
                    data-title={getLocalization(localization, "Currency")}
                  >
                    {item.currencyUnit} {item.curencyIso}
                  </div>
                  <div
                    className="table-exrate-cell"
                    data-title={getLocalization(localization, "Buy")}
                  >
                    {type === ExchangeRatesTableType.Cash ? item.cashBuyStr : item.nonCashBuyStr}
                  </div>
                  <div
                    className="table-exrate-cell"
                    data-title={getLocalization(localization, "Sell")}
                  >
                    {type === ExchangeRatesTableType.Cash ? item.cashSellStr : item.nonCashSellStr}
                  </div>
                  <div
                    className="table-exrate-cell penultimate"
                    data-title={getLocalization(localization, "CnbMiddle")}
                  >
                    {item.middleCNBStr}
                  </div>
                  <div className="table-exrate-cell table-exrate-icon-arrow-table">
                    <Icon
                      name={arrowRight.name}
                      viewBox={arrowRight.viewbox}
                      alt={arrowRight.alt}
                      svg={arrowRight.svg}
                      className="inline h-8 w-8 fill-red-corporate"
                    />
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default memo(RatesTable);
